// The incremental half of the tiered MySQL snapshot load (SLICES.md § S7
// items 1 + 2, engine parity from item 8). `mysqlSchemaCache.ts` owns the
// cache entry, the TTL and the phase orchestration; this module owns the DATA
// STRUCTURE those phases fill in.
//
// Mirrors `mssqlSnapshotMerge.ts` on top of `liveSnapshotMerge.ts`. Two MySQL
// specifics:
//
//   * `tables` and `views` are separate arrays in the native shape but ONE
//     projected relation list. They share entry identity with `entries`, so a
//     column page mutating an entry is visible through whichever array holds
//     it.
//   * the bus `foreignKeys` are DERIVED from each relation's FK rows. Rather
//     than re-deriving the whole list per page (quadratic), the merge appends
//     each constraint's edge as its page lands — safe because the paged loop
//     never splits a `(table, constraint)` group across two pages.

import type {
  FkEdge,
  RoutineMeta,
  SchemaSnapshot,
  TableMeta,
} from '@/core/engines/intellisenseTypes';
import type { RoutineMetadata } from '@/types/electron';
import { projectMysqlTableEntry } from '@/features/intellisense/schemaBus/adapters/mysqlAdapter';
import { projectRoutines } from '@/features/intellisense/schemaBus/adapters/projectionHelpers';

import { forEachChunkYielding } from './introspectionPaging';
import {
  applyGlobalPage,
  applyPerRelationPage,
  claimGlobalReplace,
  createLiveSnapshot,
  type LiveSnapshot,
  reconcilePerRelationArtefact,
} from './liveSnapshotMerge';
import type { MysqlRelationScoped } from './mysqlSchemaIntrospection';
import type {
  MysqlSchemaColumn,
  MysqlSchemaForeignKey,
  MysqlSchemaIndex,
  MysqlSchemaObject,
  MysqlSchemaRoutine,
  MysqlSchemaSnapshot,
} from './mysqlSchemaTypes';

export type MysqlLiveSnapshot = LiveSnapshot<MysqlSchemaSnapshot, MysqlSchemaObject> & {
  /** Mutable array backing `projected.foreignKeys`. */
  readonly projectedForeignKeys: FkEdge[];
  /** Mutable array backing `projected.routines`. */
  readonly projectedRoutines: RoutineMeta[];
  /** Mutable array shared by identity with `native.routines`. */
  readonly routines: MysqlSchemaRoutine[];
};

/** MySQL relation names fold case per `lower_case_table_names`; the cache's
 *  own lookups have always been lower-cased, so the merge key is too. */
export function mysqlTableKey(name: string): string {
  return name.toLowerCase();
}

/**
 * Carry the previous refresh's columns / indexes / FK rows onto the freshly
 * fetched relation list — stale-while-revalidate. Without this, every forced
 * refresh would blank every relation's columns for as long as phase 2 takes.
 */
export function carryOverMysqlIntrospection(
  previous: readonly MysqlSchemaObject[] | undefined,
  fresh: readonly { name: string; type: MysqlSchemaObject['type'] }[],
): MysqlSchemaObject[] {
  const byKey = new Map<string, MysqlSchemaObject>();
  for (const entry of previous ?? []) byKey.set(mysqlTableKey(entry.name), entry);
  return fresh.map((entry) => {
    const prior = byKey.get(mysqlTableKey(entry.name));
    return {
      name: entry.name,
      type: entry.type,
      columns: prior ? prior.columns.slice() : [],
      indexes: prior ? prior.indexes.slice() : [],
      foreignKeys: prior ? prior.foreignKeys.slice() : [],
    };
  });
}

/**
 * Seed the three bare-name lookup indexes from the live relation entries, in
 * event-loop-yielding chunks. Runs ONCE per refresh, over whatever columns the
 * carry-over brought along (nothing at all on a cold load). Phase 2 keeps the
 * indexes in step incrementally from there — a full rebuild per page would be
 * quadratic in the catalog size and would blow the long-task budget.
 */
async function seedLookups(
  live: MysqlLiveSnapshot,
  yieldBetweenChunks?: () => Promise<void>,
): Promise<void> {
  const native = live.native;
  native.objectsByNameLower = {};
  native.columnsByObjectNameLower = {};
  native.columnIndexByNameLower = {};
  await forEachChunkYielding(
    live.entries,
    (chunk) => {
      for (const obj of chunk) indexRelation(live, obj);
    },
    { yieldBetweenChunks },
  );
}

/** Register one relation (and its current columns) in the lookup indexes. */
function indexRelation(live: MysqlLiveSnapshot, obj: MysqlSchemaObject): void {
  const native = live.native;
  const key = mysqlTableKey(obj.name);
  native.objectsByNameLower[key] = obj;
  native.columnsByObjectNameLower[key] = obj.columns;
  for (const column of obj.columns) {
    (native.columnIndexByNameLower[column.name.toLowerCase()] ??= []).push({
      objectName: obj.name,
      objectType: obj.type,
      column,
    });
  }
}

/** Drop one relation's contributions to the column-name index (O(bucket)). */
function unindexColumns(
  live: MysqlLiveSnapshot,
  obj: MysqlSchemaObject,
  columns: readonly MysqlSchemaColumn[],
): void {
  const index = live.native.columnIndexByNameLower;
  for (const column of columns) {
    const bucketKey = column.name.toLowerCase();
    const bucket = index[bucketKey];
    if (!bucket) continue;
    const kept = bucket.filter((row) => row.objectName !== obj.name);
    if (kept.length === 0) delete index[bucketKey];
    else index[bucketKey] = kept;
  }
}

/**
 * Build the live snapshot for a freshly fetched relation list, projecting the
 * relations in event-loop-yielding chunks so even a 200k-relation catalog
 * never occupies the main thread for a single long task.
 */
export async function createMysqlLiveSnapshot(
  native: MysqlSchemaSnapshot,
  options?: { readonly yieldBetweenChunks?: () => Promise<void> },
): Promise<MysqlLiveSnapshot> {
  const entries = native.tables.concat(native.views);
  const routines = native.routines.slice();
  native.routines = routines;

  const projectedForeignKeys: FkEdge[] = [];
  const projectedRoutines: RoutineMeta[] = projectRoutines(
    native.routinesByNameLower,
    native.database,
  );

  const base = await createLiveSnapshot<MysqlSchemaSnapshot, MysqlSchemaObject>({
    native,
    entries,
    keyOf: (entry) => mysqlTableKey(entry.name),
    projectEntry: (entry) => projectMysqlTableEntry(entry, native.database),
    buildProjected: (tables: TableMeta[]): SchemaSnapshot => ({
      engineId: 'mysql',
      connectionId: native.connectionId,
      database: native.database,
      schemas: native.databases ?? [],
      tables,
      routines: projectedRoutines,
      foreignKeys: projectedForeignKeys,
      serverVersion: native.serverVersion || undefined,
      builtAt: native.builtAt,
    }),
    yieldBetweenChunks: options?.yieldBetweenChunks,
  });

  // The native `tables` / `views` arrays must hold the SAME entry objects the
  // live snapshot indexes, so a consumer reading the native cache sees phase
  // 2's merges.
  native.tables = base.entries.filter((e) => e.type === 'table');
  native.views = base.entries.filter((e) => e.type === 'view');

  const live: MysqlLiveSnapshot = {
    ...base,
    projectedForeignKeys,
    projectedRoutines,
    routines,
  };
  await seedLookups(live, options?.yieldBetweenChunks);
  // Carried-over FK rows still describe live relations, so seed the projected
  // edge list from them; the first FK page of the refresh clears it.
  for (const entry of live.entries) {
    projectedForeignKeys.push(...projectMysqlEdges(entry));
  }
  return live;
}

/** Group one relation's FK rows into `FkEdge`s (same rule as the adapter). */
function projectMysqlEdges(table: MysqlSchemaObject): FkEdge[] {
  const byConstraint = new Map<
    string,
    { fromColumns: string[]; toTable: string; toColumns: string[] }
  >();
  for (const fk of table.foreignKeys) {
    const existing = byConstraint.get(fk.constraintName);
    if (existing) {
      existing.fromColumns.push(fk.columnName);
      existing.toColumns.push(fk.referencedColumn);
    } else {
      byConstraint.set(fk.constraintName, {
        fromColumns: [fk.columnName],
        toTable: fk.referencedTable,
        toColumns: [fk.referencedColumn],
      });
    }
  }
  return Array.from(byConstraint.entries()).map(([name, edge]) => ({
    fromTable: table.name,
    fromColumns: edge.fromColumns,
    toTable: edge.toTable,
    toColumns: edge.toColumns,
    name,
  }));
}

/**
 * Merge one page of column rows, replacing carried-over columns relation by
 * relation, and keep the bare-name lookup indexes in step incrementally — the
 * work per page scales with the PAGE, not with the catalog.
 */
export function applyMysqlColumnPage(
  live: MysqlLiveSnapshot,
  columns: readonly MysqlRelationScoped<MysqlSchemaColumn>[],
): void {
  applyPerRelationPage(live, 'columns', columns, {
    keyOf: (c) => mysqlTableKey(c.table),
    append: (entry, list, replace) => {
      const projected = list.map((c) => c.item);
      if (replace) {
        // The carried-over columns are being dropped — take their rows out of
        // the column-name index before they become unreachable.
        unindexColumns(live, entry, entry.columns);
        // Never mutates the existing array: a carried-over `columns` may still
        // be shared with the previous snapshot object a caller is holding.
        entry.columns = projected;
      } else {
        entry.columns = [...entry.columns, ...projected];
      }
      const key = mysqlTableKey(entry.name);
      live.native.objectsByNameLower[key] = entry;
      live.native.columnsByObjectNameLower[key] = entry.columns;
      for (const column of projected) {
        (live.native.columnIndexByNameLower[column.name.toLowerCase()] ??= []).push({
          objectName: entry.name,
          objectType: entry.type,
          column,
        });
      }
    },
  });
}

export function applyMysqlIndexPage(
  live: MysqlLiveSnapshot,
  indexes: readonly MysqlRelationScoped<MysqlSchemaIndex>[],
): void {
  applyPerRelationPage(live, 'indexes', indexes, {
    keyOf: (i) => mysqlTableKey(i.table),
    append: (entry, list, replace) => {
      const projected = list.map((i) => i.item);
      entry.indexes = replace ? projected : [...entry.indexes, ...projected];
    },
  });
}

/**
 * After the index artefact was read completely, relations the refresh never
 * touched have no indexes any more — drop the ones carried over from the
 * previous refresh (release review 1.5.0, F5).
 */
export function reconcileMysqlIndexes(live: MysqlLiveSnapshot): void {
  reconcilePerRelationArtefact(live, 'indexes', (entry) => {
    if (entry.indexes.length === 0) return false;
    entry.indexes = [];
    return true;
  });
}

/**
 * FK rows go onto their relation (replacing carried-over rows relation by
 * relation) AND onto the flat bus edge list. The flat list is cleared by the
 * FIRST page of the refresh and appended to thereafter — each constraint's
 * edge is emitted exactly once because the paged loop never splits a
 * `(table, constraint)` group.
 */
export function applyMysqlForeignKeyPage(
  live: MysqlLiveSnapshot,
  fks: readonly MysqlRelationScoped<MysqlSchemaForeignKey>[],
): void {
  applyPerRelationPage(live, 'foreignKeys', fks, {
    keyOf: (f) => mysqlTableKey(f.table),
    append: (entry, list, replace) => {
      const projected = list.map((f) => f.item);
      entry.foreignKeys = replace ? projected : [...entry.foreignKeys, ...projected];
    },
  });

  const edges: FkEdge[] = [];
  const byTable = new Map<string, MysqlSchemaForeignKey[]>();
  for (const { table, item } of fks) {
    const list = byTable.get(table);
    if (list) list.push(item);
    else byTable.set(table, [item]);
  }
  for (const [table, rows] of byTable) {
    edges.push(...projectMysqlEdges({ name: table, type: 'table', columns: [], indexes: [], foreignKeys: rows }));
  }
  applyGlobalPage(live, 'projectedForeignKeys', live.projectedForeignKeys, edges);
}

/** The routine NAME list (`information_schema.ROUTINES`). */
export function applyMysqlRoutineListPage(
  live: MysqlLiveSnapshot,
  routines: readonly MysqlSchemaRoutine[],
): void {
  applyGlobalPage(live, 'routines', live.routines, routines);
}

/**
 * Routine SIGNATURES (`routines ⋈ parameters`), bucketed by lower-cased name.
 * The first page of a refresh drops the carried-over map so a dropped routine
 * disappears; later pages append.
 */
export function applyMysqlRoutineParamPage(
  live: MysqlLiveSnapshot,
  routines: readonly RoutineMetadata[],
): void {
  if (claimGlobalReplace(live, 'routineParams')) {
    live.native.routinesByNameLower = {};
  }
  const byName = live.native.routinesByNameLower;
  for (const routine of routines) {
    (byName[routine.name.toLowerCase()] ??= []).push(routine);
  }
  live.projectedRoutines.length = 0;
  live.projectedRoutines.push(...projectRoutines(byName, live.native.database));
}
